import { useCallback, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  dceUploadService,
  type UploadProgressEvent,
} from '../services/dce-upload.service';
import type {
  DceDocumentType,
  DceSearchRequest,
  DceUpload,
  DceSourceType,
} from '../types/dce-upload.types';
import { isTerminalStatus } from '../types/dce-upload.types';

const QK = {
  uploads: (opportunityId: number) => ['tm', 'dce-uploads', opportunityId],
  upload: (uploadId: number) => ['tm', 'dce-upload', uploadId],
  documents: (uploadId: number) => ['tm', 'dce-upload-documents', uploadId],
};

/** Liste des uploads DCE d'une opportunité (TanStack Query). */
export function useDceUploads(opportunityId: number) {
  return useQuery({
    queryKey: QK.uploads(opportunityId),
    queryFn: () => dceUploadService.listByOpportunity(opportunityId),
    enabled: !!opportunityId,
  });
}

/**
 * Polling d'un upload pour suivre la progression du pipeline.
 * Refetch toutes les 2s tant que le statut n'est pas terminal.
 */
export function useDceUploadStatus(uploadId: number | null) {
  return useQuery({
    queryKey: uploadId ? QK.upload(uploadId) : ['tm', 'dce-upload', 'noop'],
    queryFn: () => dceUploadService.getById(uploadId as number),
    enabled: uploadId !== null,
    refetchInterval: (query) => {
      const data = query.state.data as DceUpload | undefined;
      if (!data) return 2000;
      return isTerminalStatus(data.status) ? false : 2000;
    },
  });
}

/** Liste les OpportunityDocument issus d'un upload (lazy : enabled à la demande). */
export function useDceUploadDocuments(uploadId: number | null, enabled = true) {
  return useQuery({
    queryKey: uploadId
      ? QK.documents(uploadId)
      : ['tm', 'dce-upload-documents', 'noop'],
    queryFn: () => dceUploadService.listDocuments(uploadId as number),
    enabled: uploadId !== null && enabled,
  });
}

/** Reclassement manuel — invalide la query des documents de l'upload. */
export function useReclassifyDceDocument(uploadId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: {
      documentId: number;
      dceDocumentType: DceDocumentType;
      lotId?: number | null;
    }) =>
      dceUploadService.reclassifyDocument(
        uploadId,
        input.documentId,
        input.dceDocumentType,
        input.lotId,
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.documents(uploadId) });
    },
  });
}

/** Approbation finale d'une classification (pose classificationApprovedAt). */
export function useApproveDceClassification(opportunityId: number, uploadId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => dceUploadService.approveClassification(uploadId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.uploads(opportunityId) });
      qc.invalidateQueries({ queryKey: QK.upload(uploadId) });
    },
  });
}

/** Recherche RAG dans le DCE d'une opportunité. Mutation (pas de cache). */
export function useDceSearch(opportunityId: number) {
  return useMutation({
    mutationFn: (req: DceSearchRequest) =>
      dceUploadService.searchDce(opportunityId, req),
  });
}

export function useDeleteDceUpload(opportunityId: number) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (uploadId: number) => dceUploadService.softDelete(uploadId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK.uploads(opportunityId) });
    },
  });
}

export interface RunDceUploadParams {
  opportunityId: number;
  sourceType: DceSourceType;
  files: File[];
  parentUploadId?: number;
  onProgress?: (event: UploadProgressEvent) => void;
}

export interface RunDceUploadState {
  isRunning: boolean;
  uploadId: number | null;
  error: Error | null;
}

/**
 * Orchestrateur d'upload :
 *   1. POST /presign → récupère N URLs présignées
 *   2. PUT direct vers MinIO en parallèle (3 concurrents max)
 *   3. POST /confirm → déclenche le pipeline backend
 *
 * Le polling du statut est ensuite assuré par useDceUploadStatus.
 */
export function useRunDceUpload() {
  const qc = useQueryClient();
  const [state, setState] = useState<RunDceUploadState>({
    isRunning: false,
    uploadId: null,
    error: null,
  });

  const run = useCallback(
    async (params: RunDceUploadParams): Promise<number> => {
      setState({ isRunning: true, uploadId: null, error: null });
      try {
        if (params.files.length === 0) {
          throw new Error('Aucun fichier sélectionné');
        }
        if (params.sourceType === 'ZIP' && params.files.length !== 1) {
          throw new Error('sourceType=ZIP requiert exactement 1 fichier');
        }

        const presign = await dceUploadService.createPresignedUpload(
          params.opportunityId,
          {
            sourceType: params.sourceType,
            files: params.files.map((f) => ({
              filename: f.name,
              sizeBytes: f.size,
              contentType: f.type || undefined,
            })),
            parentUploadId: params.parentUploadId,
          },
        );

        setState((s) => ({ ...s, uploadId: presign.uploadId }));

        // Upload parallèle avec une limite de 3 concurrents
        await runWithConcurrency(presign.files, 3, async (presignedFile, index) => {
          const file = params.files[index];
          if (!file) throw new Error(`Fichier manquant pour index ${index}`);
          await dceUploadService.uploadFileToPresignedUrl(
            file,
            presignedFile.url,
            params.onProgress,
            index,
          );
        });

        await dceUploadService.confirmUpload(
          params.opportunityId,
          presign.uploadId,
          presign.files.map((f) => f.path),
        );

        qc.invalidateQueries({ queryKey: QK.uploads(params.opportunityId) });
        setState({ isRunning: false, uploadId: presign.uploadId, error: null });
        return presign.uploadId;
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err));
        setState({ isRunning: false, uploadId: null, error });
        throw error;
      }
    },
    [qc],
  );

  return { ...state, run };
}

async function runWithConcurrency<T>(
  items: T[],
  concurrency: number,
  worker: (item: T, index: number) => Promise<void>,
): Promise<void> {
  const queue = items.map((item, index) => ({ item, index }));
  const workers = Array.from({ length: Math.min(concurrency, items.length) }, async () => {
    while (queue.length > 0) {
      const next = queue.shift();
      if (!next) break;
      await worker(next.item, next.index);
    }
  });
  await Promise.all(workers);
}
